// Adapted from pinpointedu/frontend/apps/product/src/next/app/errors/ProductRouteStates.tsx
// (§9 of the frontend proposal): a named `kind` union, a focused <h1>, a real
// recovery action, and the assertion carried over verbatim -- "No
// demonstration data has been substituted." MoneySkyline adds the
// finance-specific kinds from §3's honest-route-states table. M0 renders a
// handful of these for real; the rest exist so later PRs have a stable,
// tested surface to render into.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export type RouteStateKind =
  | 'not-found'
  | 'api-unavailable'
  | 'offline'
  | 'session-check-failure'
  | 'no-accounts'
  | 'first-sync-running'
  | 'item-needs-login'
  | 'item-revoked'
  | 'sync-failed'
  | 'draft-expired'
  | 'stale-snapshot'
  | 'likely-transfer-review'
  | 'not-built-yet'
  // Added 2026-09-06 (UX execution plan §6.1). `snapshot-gap` and
  // `low-confidence` are deliberately NOT here -- they are inline/datum-level
  // treatments, not screen states (see the plan's "deliberately NOT new
  // kinds" list).
  | 'no-data-in-range'
  | 'awaiting-first-snapshot'
  | 'preview-superseded'
  | 'item-pending-expiration'
  | 'inbox-zero'
  | 'business-unavailable'
  | 'conditions-unavailable'
  | 'export-unavailable'
  | 'event-unavailable'
  | 'settings-unavailable';

// Single source of truth for honest-state copy. Exported so inline
// renderings (components/HonestInline.tsx) reuse the exact wording rather
// than keeping a second, drifting copy of it.
export const ROUTE_STATE_CONTENT: Record<RouteStateKind, { title: string; body: string }> = {
  'not-found': {
    title: 'Page not found',
    body: 'That page is not part of MoneySkyline. No demonstration data has been substituted.',
  },
  'api-unavailable': {
    title: 'MoneySkyline is temporarily unavailable',
    body: 'We could not load current data. No demonstration data has been substituted.',
  },
  offline: {
    title: 'You appear to be offline',
    body: 'Reconnect to the internet, then retry. No demonstration data has been substituted.',
  },
  'session-check-failure': {
    title: "We couldn't verify your session",
    body: 'Retry the check or sign in again.',
  },
  'no-accounts': {
    title: 'No accounts connected yet',
    body: 'Connect a bank to see your net worth here.',
  },
  'first-sync-running': {
    title: 'First sync is still running',
    body: 'Your transaction history is still arriving. This is not an empty feed.',
  },
  'item-needs-login': {
    title: 'This connection needs your attention',
    body: 'The bank requires you to sign in again to keep syncing.',
  },
  'item-revoked': {
    title: 'This connection was removed at the bank',
    body: 'Historical rows stay visible, labelled as of the last sync.',
  },
  'sync-failed': {
    title: 'The last sync failed',
    body: 'Retry the sync or reconnect the account.',
  },
  'draft-expired': {
    title: 'This preview expired',
    body: 'Re-run the preview with the same sentence.',
  },
  'stale-snapshot': {
    title: 'This number is partial',
    body: 'Some accounts are stale or have never reported a balance.',
  },
  'likely-transfer-review': {
    title: 'Likely transfer pending review',
    body: 'Confirm the pairing or mark it as not a transfer.',
  },
  'not-built-yet': {
    title: 'Not built yet',
    body: 'This screen has not shipped. No demonstration data has been substituted.',
  },
  'no-data-in-range': {
    title: 'Nothing in this range',
    body: 'No transactions match this range and filter.',
  },
  'awaiting-first-snapshot': {
    title: 'Trend appears after the second daily snapshot',
    body: 'A second balance snapshot is needed before history can be shown.',
  },
  'preview-superseded': {
    title: '3 new matches since you previewed',
    body: 'A sync landed. Nothing was applied.',
  },
  'item-pending-expiration': {
    title: 'Access ends soon',
    body: 'Reconnect before then to avoid a gap. Nothing is wrong today.',
  },
  'inbox-zero': {
    title: 'Everything is labelled',
    body: 'New items land here after each sync.',
  },
  'business-unavailable': {
    title: 'Business',
    body: 'Business summaries aren’t available yet.',
  },
  'conditions-unavailable': {
    title: 'Conditions',
    body: 'Creating and managing conditions isn’t available yet.',
  },
  'export-unavailable': {
    title: 'Export',
    body: 'Business exports aren’t available yet.',
  },
  'event-unavailable': {
    title: 'Event',
    body: 'Saved events aren’t available to view yet.',
  },
  'settings-unavailable': {
    title: 'Settings',
    body: 'Settings aren’t available yet.',
  },
};

export type RouteRecoveryFrame = 'standalone' | 'shell-page' | 'inline';

export function RouteRecoveryScreen({ kind = 'not-found', frame = 'standalone' }: { kind?: RouteStateKind; frame?: RouteRecoveryFrame }) {
  const heading = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  useEffect(() => {
    if (frame !== 'standalone') return;
    heading.current?.focus();
  }, [frame, kind, location.pathname]);

  const { title, body } = ROUTE_STATE_CONTENT[kind] ?? ROUTE_STATE_CONTENT['not-found'];
  const headingId = `route-state-${kind}-heading`;

  const content = (
    <>
      {frame === 'inline' ? <h2 id={headingId} className="text-xl font-semibold">{title}</h2> : <h1 id={headingId} ref={heading} tabIndex={-1} className="text-2xl font-bold">{title}</h1>}
      <p className={frame === 'inline' ? 'text-muted-foreground' : 'max-w-md text-muted-foreground'} role={frame === 'inline' ? 'status' : undefined} aria-live={frame === 'inline' ? 'polite' : undefined}>{body}</p>
      <div className="flex flex-wrap justify-center gap-3">
        {(kind === 'api-unavailable' || kind === 'offline' || kind === 'session-check-failure' || kind === 'sync-failed') && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-primary-foreground"
          >
            Retry
          </button>
        )}
        {kind === 'not-found' && (
          <a href="/accounts" className="inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-primary-foreground">
            Go to Accounts
          </a>
        )}
        {(kind === 'business-unavailable' || kind === 'conditions-unavailable' || kind === 'export-unavailable' || kind === 'event-unavailable') && (
          <a href="/transactions" className="inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-primary-foreground">
            View transactions
          </a>
        )}
        {kind === 'settings-unavailable' && (
          <a href="/accounts" className="inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-primary-foreground">
            Go to Accounts
          </a>
        )}
      </div>
    </>
  );
  if (frame === 'inline') return <section aria-labelledby={headingId} className="flex flex-col items-center justify-center gap-4 text-center" data-testid={`route-state-${kind}`}>{content}</section>;
  if (frame === 'shell-page') return <section aria-labelledby={headingId} className="flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid={`route-state-${kind}`}>{content}</section>;
  return <main aria-labelledby={headingId} className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center" data-testid={`route-state-${kind}`}>{content}</main>;
}

/** Placeholder for every M1+ route: honest, never fabricated data. */
export function NotBuiltYetScreen() {
  return <RouteRecoveryScreen kind="not-built-yet" />;
}
